// A "show" is every row of the events table that shares a `show_slug` — the
// feed's one-row-per-performance-date split, regrouped. The show page
// (/[lang]/events/<show_slug>) renders one of these; the lists read show rows
// straight from the `event_shows` RPC instead (see 20261001130000_event_shows.sql).
//
// Framework-free so the build-time static params (detail/event-detail.ts, and
// riviera's own with its legacy redirects), the client hooks (hooks/use-event.ts,
// riviera's use-event.ts) and the pages all build the same object.

import { isEventPast } from './date-range';
import type { DbEvent } from './supabase';

/** One performance date of a show — just what the dates list, its CTAs and the JSON-LD read. */
export type ShowDate = Pick<
  DbEvent,
  'id' | 'source' | 'city_id' | 'venue_name' | 'start_ts' | 'end_ts' | 'tz' | 'min_price' | 'currency' | 'ticket_url' | 'sold_out'
>;

export type EventShow = {
  /**
   * The row the page reads its copy, image and category from: the soonest
   * upcoming date, or — once every date is over — the last one, so an ended
   * show still renders (and reads as ended via isEventPast) during its 30-day
   * retention window.
   */
  event: DbEvent;
  /** Upcoming dates, soonest first. Empty once the show has ended. */
  dates: ShowDate[];
};

// Old per-date URLs (/[lang]/events/<uuid>) still resolve: the route renders
// a redirect to the show page for these. Slugs are [a-z0-9-] and never this shape.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isEventUuid(param: string | undefined): boolean {
  return !!param && UUID_RE.test(param);
}

function toShowDate(e: DbEvent): ShowDate {
  return {
    id: e.id,
    source: e.source,
    city_id: e.city_id,
    venue_name: e.venue_name,
    start_ts: e.start_ts,
    end_ts: e.end_ts,
    tz: e.tz,
    min_price: e.min_price,
    currency: e.currency,
    ticket_url: e.ticket_url,
    // Only on rows read with it (dubai); absent, it stays out of the baked JSON.
    ...(e.sold_out != null ? { sold_out: e.sold_out } : {}),
  };
}

/**
 * The feed sometimes lists one date several times at the same venue (LE COMEDY
 * CLUB: 4 rows every Saturday, identical but for the product id — and it
 * carries no showtimes to tell them apart). Those collapse to one date, the
 * cheapest; `sorted` is in start_ts, id order, so ties keep the first.
 */
function uniqueDates(sorted: DbEvent[]): DbEvent[] {
  const byKey = new Map<string, DbEvent>();
  for (const e of sorted) {
    const key = `${e.start_ts}|${e.venue_name ?? ''}`;
    const kept = byKey.get(key);
    if (!kept || (e.min_price != null && (kept.min_price == null || e.min_price < kept.min_price))) {
      byKey.set(key, e);
    }
  }
  return [...byKey.values()];
}

/** Builds a show from its rows (any order). Null for no rows. */
export function buildShow(rows: DbEvent[]): EventShow | null {
  if (rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) =>
    a.start_ts < b.start_ts ? -1 : a.start_ts > b.start_ts ? 1 : a.id < b.id ? -1 : 1,
  );
  const upcoming = uniqueDates(sorted.filter((e) => !isEventPast(e)));
  return {
    event: upcoming[0] ?? sorted[sorted.length - 1],
    dates: upcoming.map(toShowDate),
  };
}

/** Whether the show's dates take place at more than one venue (e.g. a festival). */
export function showHasManyVenues(dates: ShowDate[]): boolean {
  return new Set(dates.map((d) => d.venue_name ?? '')).size > 1;
}
